// src/firebase.js

// Import Firebase modules
import { initializeApp } from "firebase/app";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { sniffImageFormat } from "./imageFormat";

// Firebase configuration for "DJs Img Storage"
const firebaseConfig = {
  apiKey: "AIzaSyB7t_noe3t9yWfQv0MW5SCzSe5odDtTUV0",
  authDomain: "djs-img-storage.firebaseapp.com",
  projectId: "djs-img-storage",
  storageBucket: "djs-img-storage.appspot.com",
  messagingSenderId: "386638406556",
  appId: "1:386638406556:web:bc8a18d108dec31e311e59"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
export const auth = getAuth(app);

// Initialize Firebase Storage
export const storage = getStorage(app);

// Fail fast instead of hanging on the SDK defaults (operation 120s / upload 600s).
// A stalled upload/getDownloadURL now surfaces in ~30–60s rather than blocking the
// onboarding submit for two minutes and then throwing storage/retry-limit-exceeded.
// In the modular SDK these are writable properties on the storage instance (there is
// no setMaxOperationRetryTime export).
storage.maxOperationRetryTime = 30000;
storage.maxUploadRetryTime = 60000;

// Downscale + re-encode an image on the client before upload. Phone photos are
// commonly 5–15MB at full resolution; shrinking to ~1600px / JPEG 0.8 typically
// yields a 20–40x smaller payload, which is the single biggest factor in whether
// concurrent uploads finish inside the retry window (and cuts later load/egress cost).
// Falls back to the original file if the image can't be decoded (e.g. HEIC on
// Chrome) or is a GIF (preserve animation) — the size guard in the caller is the backstop.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.8;
async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    // Nothing to gain if it's already small and already a JPEG. The bytes are checked
    // too: an iPhone can label a HEIC image/jpeg, and that must still be re-encoded.
    if (scale === 1 && file.type === 'image/jpeg' && (await sniffImageFormat(file)) === 'jpeg') {
      bitmap.close?.();
      return file;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob) return file; // toBlob can return null; fall back to original
    const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch (error) {
    // Undecodable format (HEIC on Chrome, etc.) — upload the original.
    console.warn('Image compression skipped; uploading original:', error?.message || error);
    return file;
  }
}

// Folder used when a caller does not name one: DJ onboarding photos, and any
// election whose Folder Path in Quickbase is blank.
const DEFAULT_UPLOAD_FOLDER = 'WI';

// Storage folder for an upload: the given path with surrounding whitespace and
// slashes removed ("/2026" -> "2026"), or the default when blank.
export function resolveUploadFolder(folder) {
  const trimmed = typeof folder === 'string' ? folder.trim().replace(/^\/+|\/+$/g, '') : '';
  return trimmed || DEFAULT_UPLOAD_FOLDER;
}

// Upload a file to Firebase Storage under `folder` (see resolveUploadFolder).
export async function uploadFile(file, index, folder) {
  try {
    // Shrink before upload (see compressImage). Falls back to the original on failure.
    const blob = await compressImage(file);

    // Generate a unique filename using multiple unique identifiers
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    const uniqueName = `${timestamp}_${random}_${index}_${blob.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;

    const storageRef = ref(storage, `${resolveUploadFolder(folder)}/${uniqueName}`);

    // Actually upload the bytes (this was the load-bearing call), then read the URL.
    // cacheControl makes the resulting download URL browser/CDN-cacheable; filenames
    // are unique per upload, so 'immutable' with a long max-age is safe.
    const metadata = {
      contentType: blob.type,
      cacheControl: 'public,max-age=604800,immutable',
    };
    const snapshot = await uploadBytesResumable(storageRef, blob, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      fileName: uniqueName,
      metadata: {
        size: blob.size,
        contentType: blob.type,
        timeCreated: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}
